/**
 * @graphql cc
 */
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { graphql, RelayEnvironmentProvider } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import type CardClient from '@atlaskit/link-provider/client';
import { SmartCardProvider } from '@atlaskit/link-provider/smart-card-provider';
import type { CardStore } from '@atlaskit/linking-common/store';

import { getCorrectContentURL } from '@atlassian/confluence-page-preview-panel/utils/custom-domains';

import { getRelayEnvironment } from '@confluence/graphql-provider/entry-points/relayEnvironment.ts';
import { useStoreOptions } from '@confluence/content-smartlinks/entry-points/useStoreOptions.ts';
import { isPageSSRd } from '@confluence/content-smartlinks/entry-points/isPageSSRd.ts';
import { fg } from '@confluence/feature-gating';
import { getLogger } from '@confluence/logger';
import { usePageSpaceKey } from '@confluence/page-context';
import { PDF_EXPORT_PAGE } from '@confluence/named-routes/entry-points/routes.ts';
import { useIsOnRoute } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { PreviewPanelsPostMessageListener } from '@confluence/preview-panel-api/entry-points/PreviewPanelsPostMessageListener.ts';
import { useOpenPreviewPanel } from '@confluence/preview-panel-api/entry-points/useOpenPreviewPanel.ts';
import { isPreviewPanelAvailable } from '@confluence/preview-panel-api/entry-points/isPreviewPanelAvailable.ts';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import { useIsSSROrHydrating } from '@confluence/ssr-utilities/entry-points/useIsSSROrHydrating.ts';
import {
	type FragmentReaderInterop,
	useReadFragmentInterop,
	// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
} from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop.ts';
import {
	useWriteFragmentInterop,
	type FragmentWriterInterop,
	// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
} from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop.ts';

import type { ConfluenceSmartCardProviderConsolidatedSmartCardProviderReadFragment$data } from './__generated__/ConfluenceSmartCardProviderConsolidatedSmartCardProviderReadFragment.graphql.ts';
import type { ConfluenceSmartCardProviderConsolidatedSmartCardProviderWriteFragment$data } from './__generated__/ConfluenceSmartCardProviderConsolidatedSmartCardProviderWriteFragment.graphql.ts';
import ConfluenceSmartCardProviderConsolidatedSmartCardProviderWriteFragment_artifact from './__generated__/ConfluenceSmartCardProviderConsolidatedSmartCardProviderWriteFragment.graphql.ts';
import { ConfluenceSmartCardClient } from './ConfluenceSmartCardClient.ts';
import { liveDocSmartCardResolver } from './resolvers/liveDocSmartCardResolver.ts';

export type ConfluenceSmartCardProviderProps = Parameters<typeof SmartCardProvider>[0] & {
	doNotUsePreviewPanels?: boolean;
};

const MaybeConditionalRelayEnvironmentProvider = ({ children }: { children: React.ReactNode }) => (
	<RelayEnvironmentProvider environment={getRelayEnvironment()}>
		{children}
	</RelayEnvironmentProvider>
);

// Only log first error to console, to prevent spamming console with resolver errors
let hasLoggedResolverError = false;
const defaultConfluenceResolveHandler = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	readFragmentInterop: FragmentReaderInterop<any>,
	writeFragmentInterop: FragmentWriterInterop<any>,
	data?: unknown,
) => {
	try {
		// NOTE FOR DEVS OF RESOLVERS: The `data` object comes from linking platform's content resolver service which is an UNSTABLE API SCHEMA.
		// Ensure your resolver logic handles invalid schemas gracefully and is able to notify your team if the schema changes and your code needs to be updated.
		liveDocSmartCardResolver(createAnalyticsEvent, readFragmentInterop, writeFragmentInterop, data);
	} catch (resolverError) {
		// prevent errors from causing smart card provider issues
		if (!hasLoggedResolverError) {
			const logger = getLogger('ConfluenceSmartCardProvider');
			logger.error`resolver error: ${resolverError}`;
			hasLoggedResolverError = true;
		}
	}
};

const computeInitialStateFromCache = (smartCardClient: CardClient): CardStore | null => {
	// On page transition create the initial CardStore from Cache
	return !isPageSSRd() && smartCardClient instanceof ConfluenceSmartCardClient
		? smartCardClient.getInitialCardStoreFromCache()
		: null;
};

const ConsolidatedSmartCardProvider: typeof SmartCardProvider = (props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const readFragmentInterop =
		useReadFragmentInterop<ConfluenceSmartCardProviderConsolidatedSmartCardProviderReadFragment$data>(
			{
				fragmentSpec: graphql`
					fragment ConfluenceSmartCardProviderConsolidatedSmartCardProviderReadFragment on Content {
						id
						subType
						status
						__typename
					}
				`,
			},
		);
	const writeFragmentInterop =
		useWriteFragmentInterop<ConfluenceSmartCardProviderConsolidatedSmartCardProviderWriteFragment$data>(
			{
				fragmentArtifact:
					ConfluenceSmartCardProviderConsolidatedSmartCardProviderWriteFragment_artifact,
				fragmentSpec: graphql`
					fragment ConfluenceSmartCardProviderConsolidatedSmartCardProviderWriteFragment on Content {
						id
						subType
						status
						__typename
					}
				`,
			},
		);
	const smartCardClient = useMemo(
		() =>
			props?.client ||
			new ConfluenceSmartCardClient({
				onResolve: (data) =>
					defaultConfluenceResolveHandler(
						createAnalyticsEvent,
						readFragmentInterop,
						writeFragmentInterop,
						data,
					),
			}),
		[props?.client, createAnalyticsEvent, readFragmentInterop, writeFragmentInterop],
	);

	const initialStateFromCache = useRef<CardStore | null>(
		computeInitialStateFromCache(smartCardClient),
	);

	const { isAdminHubAIEnabled } = useSessionData();

	const { children, ...restProps } = props;

	const isPageSSR = isPageSSRd();
	const ssrInitialState = window['__SSR_SMARTLINKS__'];
	useEffect(() => {
		if (isPageSSR && ssrInitialState && smartCardClient instanceof ConfluenceSmartCardClient) {
			smartCardClient.seedSSRSmartLinks(ssrInitialState);
		}
	}, [isPageSSR, ssrInitialState, smartCardClient]);
	const initialState = initialStateFromCache?.current ?? ssrInitialState;
	const internalStoreOptions = useStoreOptions(initialState, isPageSSR);

	// Merge smartlink SSR data with any passed-in storeOptions (e.g. from shortcuts preloader)
	const presetInitialState = {
		...internalStoreOptions,
		...(props.storeOptions
			? {
					storeOptions: {
						...props.storeOptions,
						...(internalStoreOptions.storeOptions?.initialState
							? {
									initialState: {
										...internalStoreOptions.storeOptions.initialState,
										...props.storeOptions.initialState,
									},
								}
							: {}),
					},
				}
			: {}),
	};

	const shouldControlDataExport = useIsOnRoute(PDF_EXPORT_PAGE);

	return (
		<SmartCardProvider
			product="CONFLUENCE"
			client={smartCardClient}
			isAdminHubAIEnabled={isAdminHubAIEnabled}
			shouldControlDataExport={shouldControlDataExport}
			isPreviewPanelAvailable={restProps.isPreviewPanelAvailable}
			openPreviewPanel={restProps.openPreviewPanel}
			{...restProps}
			// props.storeOptions can be set explicitly to `undefined` -- overwriting the presetInitialState storeOptions merge with props
			{...presetInitialState}
		>
			{children}
		</SmartCardProvider>
	);
};

const PreviewPanelSmartCardProvider = ({
	children,
	...restProps
}: ConfluenceSmartCardProviderProps) => {
	const { open, isPreviewRestricted } = useOpenPreviewPanel();
	const { cloudId } = useSessionData();
	const renderServerPlaceholder = useIsSSROrHydrating();

	const isPreviewPanelAvailableForUnit = useCallback(
		({ ari }: { ari: string }) =>
			isPreviewPanelAvailable({ ari }) &&
			// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Kill switch for cross-unit preview restriction, not a measurable experiment.
			!(isPreviewRestricted({ ari }) && fg('confluence_preview_panel_unit_check')),
		[isPreviewRestricted],
	);
	const isPreviewRestrictedForUnit = useCallback(
		({ ari }: { ari: string }) =>
			isPreviewRestricted({ ari }) &&
			// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Kill switch for cross-unit preview restriction, not a measurable experiment.
			fg('confluence_preview_panel_unit_check'),
		[isPreviewRestricted],
	);
	// Create a wrapper function that updates the preview panel URL
	// This is used for custom domains to update to the correct content URL and avoid CSP issues
	const openPreviewPanelWithModifiedURL = useMemo(() => {
		if (!cloudId) {
			return open;
		}
		return ({ url, ari, ...rest }: Parameters<typeof open>[0]) =>
			open({ url: getCorrectContentURL(cloudId, url, ari), ari, ...rest });
	}, [open, cloudId]);

	return (
		<ConsolidatedSmartCardProvider
			openPreviewPanel={openPreviewPanelWithModifiedURL}
			isPreviewPanelAvailable={
				renderServerPlaceholder ? (_ari) => false : isPreviewPanelAvailableForUnit
			}
			isPreviewRestricted={renderServerPlaceholder ? undefined : isPreviewRestrictedForUnit}
			{...restProps}
		>
			{children}
			<PreviewPanelsPostMessageListener />
		</ConsolidatedSmartCardProvider>
	);
};

const ConsolidatedPreviewPanelSmartCardProvider = (props: ConfluenceSmartCardProviderProps) => {
	const [spaceKey] = usePageSpaceKey();
	const { children, doNotUsePreviewPanels, ...restProps } = props;

	return isCompanyHubSpaceKey(spaceKey) || doNotUsePreviewPanels ? (
		<ConsolidatedSmartCardProvider {...restProps}>{children}</ConsolidatedSmartCardProvider>
	) : (
		<MaybeConditionalRelayEnvironmentProvider>
			<PreviewPanelSmartCardProvider {...restProps}>{children}</PreviewPanelSmartCardProvider>
		</MaybeConditionalRelayEnvironmentProvider>
	);
};

export const ConfluenceSmartCardProvider = (
	props: ConfluenceSmartCardProviderProps,
): React.JSX.Element => {
	const sessionData = useSessionData();
	const updatedProps: ConfluenceSmartCardProviderProps = useMemo(
		() => ({
			...props,
			...(sessionData
				? {
						rovoOptions: {
							isRovoEnabled: sessionData?.isRovoEnabled ?? false,
							isRovoLLMEnabled: sessionData?.isRovoLLMEnabled ?? false,
						},
					}
				: {}),
		}),
		[props, sessionData],
	);

	return <ConsolidatedPreviewPanelSmartCardProvider {...updatedProps} />;
};
